import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useStuckOpportunities } from '@/hooks/use-api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { ReportFilters } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import {
  Download,
  AlertTriangle,
  DollarSign,
  CalendarClock,
  Hourglass,
  Loader2,
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { CHART_COLORS, ROTATED_LABEL_CHART_MARGIN } from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import type { StuckOpportunity, StuckSeverityTier } from '@/types'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { explainError } from '@/lib/explain-error'

// Open pipeline stages (mirrors backend STAGE_THRESHOLDS); used for the stage filter.
const OPEN_STAGES = [
  'New',
  'Need Identified',
  'Presentation Complete',
  'Initial Quote Complete',
  'Final Quote',
  'Verbal Received',
]

const TIER_VARIANT: Record<StuckSeverityTier, 'destructive' | 'warning' | 'secondary'> = {
  Critical: 'destructive',
  Warning: 'warning',
  Monitor: 'secondary',
}

const TIER_LABEL: Record<StuckSeverityTier, string> = {
  Critical: 'Overdue',
  Warning: 'Going cold',
  Monitor: 'Never worked',
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { dateStyle: 'medium' })
}

export default function StuckOpportunities() {
  const [filters, setFilters] = useState<ReportFilterValues>({})
  const [stage, setStage] = useState('all')
  const [minTier, setMinTier] = useState('all')

  const { data, isLoading, error } = useStuckOpportunities({
    owner_id: filters.owner_id,
    ...(stage !== 'all' ? { stage_name: stage } : {}),
    ...(minTier !== 'all' ? { min_tier: minTier } : {}),
  })

  const summary = data?.summary
  const results = useMemo(() => data?.results ?? [], [data])
  const hasFallbackRows = useMemo(
    () => results.some((r) => r.time_source === 'created_date'),
    [results]
  )

  const byStageChart = useMemo(
    () =>
      (data?.by_stage ?? []).map((s) => ({
        stage: s.stage_name,
        value: s.value_at_risk_cad,
        count: s.count,
      })),
    [data]
  )

  const byOwnerChart = useMemo(
    () =>
      (data?.by_owner ?? []).slice(0, 10).map((o) => ({
        owner: o.owner_name,
        value: o.value_at_risk_cad,
        count: o.count,
      })),
    [data]
  )

  const handleExportCSV = () => {
    if (results.length === 0) return
    const exportData = results.map((r: StuckOpportunity) => ({
      Severity: r.severity_tier,
      Opportunity: r.name,
      Account: r.account_name ?? '',
      Owner: r.owner_name ?? '',
      Stage: r.stage_name,
      'Days in Stage': r.days_in_stage,
      'Days in Stage Estimated': r.time_source === 'created_date' ? 'Yes' : 'No',
      'Days Over Threshold': r.days_over_threshold,
      'Last Activity': r.has_activities ? formatDate(r.last_activity_date) : 'Never',
      'Days Since Activity': r.days_since_activity ?? '',
      'Close Date': formatDate(r.close_date),
      'Days Overdue': r.days_overdue,
      Probability: r.probability,
      Amount: r.amount,
      Currency: r.currency_iso_code ?? '',
      'Amount (CAD)': r.amount_cad,
      'Severity Score': r.severity_score,
      Reasons: r.stuck_reasons.join('; '),
    }))
    exportToCSV({ data: exportData, filename: 'stuck-opportunities.csv' })
  }

  if (error) {
    return (
      <main className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Stuck Opportunities</h1>
          <p className="text-muted-foreground">
            Open deals stalling in the pipeline, ranked by value at risk
          </p>
        </div>
        <Card>
          <CardContent className="pt-6 space-y-4">
            <p className="text-destructive font-medium">Failed to load stuck opportunities.</p>
            <p className="text-sm text-muted-foreground">
              {explainError(error, 'Unknown error occurred')}
            </p>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              aria-label="Reload page"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </main>
    )
  }

  return (
    <main className="space-y-6">
      {/* Header + filters */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Stuck Opportunities</h1>
          <p className="text-muted-foreground">
            Open deals aged past their stage and showing a neglect or overdue signal, ranked by
            value at risk
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <ReportFilters value={filters} onChange={setFilters} showPeriod={false} />
          <div>
            <Label htmlFor="stuck-stage" className="text-xs text-muted-foreground mb-1 block">
              Stage
            </Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger
                id="stuck-stage"
                className="w-[190px] h-9"
                aria-label="Filter by stage"
              >
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {OPEN_STAGES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="stuck-tier" className="text-xs text-muted-foreground mb-1 block">
              Severity
            </Label>
            <Select value={minTier} onValueChange={setMinTier}>
              <SelectTrigger
                id="stuck-tier"
                className="w-[170px] h-9"
                aria-label="Filter by severity"
              >
                <SelectValue placeholder="All Severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="Critical">Overdue only</SelectItem>
                <SelectItem value="Warning">Going cold &amp; worse</SelectItem>
                <SelectItem value="Monitor">All (incl. never worked)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            disabled={isLoading || results.length === 0}
            aria-label="Export stuck opportunities to CSV"
            className="self-end"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin" aria-label="Loading stuck opportunities" />
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Stuck</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.total_stuck ?? 0}</div>
                <p className="text-xs text-muted-foreground">open deals needing attention</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Value at Risk</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(summary?.value_at_risk_cad ?? 0, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground">total open amount (CAD)</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Overdue</CardTitle>
                <CalendarClock className="h-4 w-4 text-destructive" aria-hidden="true" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.overdue_count ?? 0}</div>
                <p className="text-xs text-muted-foreground">past their close date</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Days Over</CardTitle>
                <Hourglass className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.avg_days_over ?? 0}</div>
                <p className="text-xs text-muted-foreground">days past stage threshold</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Value at Risk by Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={byStageChart} margin={ROTATED_LABEL_CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" angle={-15} textAnchor="end" height={50} fontSize={11} />
                    <YAxis tickFormatter={(v) => `$${Math.round(v / 1000)}k`} fontSize={11} />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(v: number, name) =>
                        name === 'Value at Risk' ? formatCurrency(v, 'CAD') : v
                      }
                    />
                    <Bar dataKey="value" name="Value at Risk" fill={CHART_COLORS[0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Stuck Deals by Owner (top 10)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={byOwnerChart}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" fontSize={11} allowDecimals={false} />
                    <YAxis type="category" dataKey="owner" width={120} fontSize={11} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" name="Stuck deals" fill={CHART_COLORS[1]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Detail table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Stuck Opportunities
                {data?.truncated ? ' (showing top 500 by severity)' : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  No stuck opportunities found. Deals are progressing on schedule.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" aria-label="Stuck opportunities detail">
                    <thead>
                      <tr className="border-b text-left">
                        <th scope="col" className="p-2 font-medium">
                          Severity
                        </th>
                        <th scope="col" className="p-2 font-medium">
                          Opportunity
                        </th>
                        <th scope="col" className="p-2 font-medium">
                          Account
                        </th>
                        <th scope="col" className="p-2 font-medium">
                          Owner
                        </th>
                        <th scope="col" className="p-2 font-medium">
                          Stage
                        </th>
                        <th scope="col" className="p-2 font-medium text-right">
                          Days in Stage
                        </th>
                        <th scope="col" className="p-2 font-medium">
                          Last Activity
                        </th>
                        <th scope="col" className="p-2 font-medium">
                          Close Date
                        </th>
                        <th scope="col" className="p-2 font-medium text-right">
                          Amount (CAD)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r) => (
                        <tr key={r.id} className="border-b hover:bg-muted/50">
                          <td className="p-2">
                            <Badge variant={TIER_VARIANT[r.severity_tier]}>
                              {TIER_LABEL[r.severity_tier]}
                            </Badge>
                          </td>
                          <td className="p-2">
                            <Link
                              to={`/opportunities/${r.id}`}
                              className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary rounded"
                            >
                              {r.name}
                            </Link>
                          </td>
                          <td className="p-2">
                            {r.account_id ? (
                              <Link
                                to={`/accounts/${r.account_id}`}
                                className="hover:underline focus:outline-none focus:ring-2 focus:ring-primary rounded"
                              >
                                {r.account_name ?? '—'}
                              </Link>
                            ) : (
                              (r.account_name ?? '—')
                            )}
                          </td>
                          <td className="p-2">{r.owner_name ?? '—'}</td>
                          <td className="p-2">{r.stage_name}</td>
                          <td className="p-2 text-right tabular-nums">
                            {r.days_in_stage}
                            {r.time_source === 'created_date' && (
                              <span
                                title="Estimated from the opportunity creation date (no stage-change history available)"
                                aria-label="estimated"
                                className="text-muted-foreground"
                              >
                                {' '}
                                *
                              </span>
                            )}
                            {r.days_over_threshold > 0 && (
                              <span className="block text-xs text-muted-foreground">
                                {r.days_over_threshold}d over
                              </span>
                            )}
                          </td>
                          <td className="p-2">
                            {r.has_activities ? (
                              formatDate(r.last_activity_date)
                            ) : (
                              <Badge variant="secondary">Never</Badge>
                            )}
                          </td>
                          <td className="p-2">
                            {formatDate(r.close_date)}
                            {r.days_overdue > 0 && (
                              <Badge variant="destructive" className="ml-1">
                                {r.days_overdue}d overdue
                              </Badge>
                            )}
                          </td>
                          <td className="p-2 text-right tabular-nums">
                            {formatCurrency(r.amount_cad, 'CAD')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {hasFallbackRows && (
                <p className="mt-3 text-xs text-muted-foreground">
                  * Time-in-stage estimated from the opportunity creation date because no
                  stage-change history is available for that deal.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </main>
  )
}
